import React, { useMemo, useRef } from 'react';
import { useEffect } from 'react';
import "antd/dist/antd.less";
import './App.less';
import { verifyIntelligentCaptcha } from '@api/login.js'
import { message } from 'antd';
import { getQueryVariable } from '@util/utils';
import { codeToLocale } from '@util/langConfig';
import uuid from "react-uuid";
import { useTranslation } from "react-i18next";

const ALIYUN_CAPTCHA_COPY = {
    cn: {
        START_VERIFY: "点击开始验证",
        POPUP_TITLE: "请完成安全验证",
        SLIDE_TIP: "请按住滑块，拖动到最右边",
        CHECK_BOX_TIP: "确认您不是机器人",
        PUZZLE_TIP: "请拖动滑块完成拼图",
        INPAINTING_TIP: "请拖动滑块还原完整图片",
        VERIFYING: "验证中...",
        SUCCESS: "验证通过!",
        SLIDE_FAIL: "验证失败，请刷新重试",
        CAPTCHA_FAIL: "验证失败，请重试!",
        CONGESTION: "网络不给力，请刷新重试",
        CAPTCHA_COMPLETED: "滑动完成",
        FINISH_CAPTCHA: "请先完成验证！",
        OVERTIME_TIP: "验证超时，请关闭验证码重试",
        OVERTIME_TIP_BOX: "验证超时，请点击验证码框体重试",
    },
    en: {
        START_VERIFY: "Click to start verification",
        POPUP_TITLE: "Please complete captcha",
        SLIDE_TIP: "Drag the slider all the way to the right.",
        CHECK_BOX_TIP: "Confirm you are not a robot",
        PUZZLE_TIP: "Drag the slider to complete the puzzle.",
        INPAINTING_TIP: "Drag the slider to restore the image.",
        VERIFYING: "Verifying...",
        SUCCESS: "Verified.",
        SLIDE_FAIL: "Verification failed. Refresh and try again.",
        CAPTCHA_FAIL: "Verification failed. Try again.",
        CONGESTION: "Network error. Please refresh and try again.",
        CAPTCHA_COMPLETED: "Complete.",
        FINISH_CAPTCHA: "Please complete the security verification first.",
        OVERTIME_TIP: "Verification timed out. Please close the Captcha and try again.",
        OVERTIME_TIP_BOX: "Verification timed out. Please click the Captcha box to try again.",
    },
    ja: {
        START_VERIFY: "クリックして認証を開始",
        POPUP_TITLE: "セキュリティ認証を完了してください",
        SLIDE_TIP: "スライダーを右端までドラッグしてください",
        CHECK_BOX_TIP: "ロボットではないことを確認してください",
        PUZZLE_TIP: "スライダーをドラッグしてパズルを完成させてください",
        INPAINTING_TIP: "スライダーをドラッグして画像を完成させてください",
        VERIFYING: "認証中...",
        SUCCESS: "認証に成功しました。",
        SLIDE_FAIL: "認証に失敗しました。更新してもう一度お試しください。",
        CAPTCHA_FAIL: "認証に失敗しました。もう一度お試しください。",
        CONGESTION: "ネットワークエラーが発生しました。更新してもう一度お試しください。",
        CAPTCHA_COMPLETED: "スライドが完了しました",
        FINISH_CAPTCHA: "先にセキュリティ認証を完了してください。",
        OVERTIME_TIP: "認証がタイムアウトしました。閉じてもう一度お試しください。",
        OVERTIME_TIP_BOX: "認証がタイムアウトしました。認証ボックスをクリックしてもう一度お試しください。",
    },
};

const getCaptchaLanguage = (language) => {
    const normalizedLanguage = String(language || 'zh').toLowerCase();
    if (normalizedLanguage.startsWith('en')) return 'en';
    if (normalizedLanguage.startsWith('ja')) return 'ja';
    if (normalizedLanguage.includes('tw') || normalizedLanguage.includes('hk')) return 'tw';
    return 'cn';
};

const App = ({
    mode = 'embed', //embed嵌入式 ｜ popup弹出式
    immediate = true,
    callBack,
    phone,
    button = '#captcha-button', // 触发验证码弹窗的元素
}) => {
    const { i18n } = useTranslation();
    const isUsedForApp = window.location.href.indexOf('/aliyun_captcha.html') != -1
    let phoneNemberFromApp = isUsedForApp ? getQueryVariable('phone') ?? '' : ''

    let captcha;

    const phoneRef = useRef(null);

    useEffect(()=>{
        phoneRef.current = phone;
    },[phone])

    const element = useMemo(()=>{
        return  'element_id_' +uuid()    
    },[button])

    const currentLanguage =
        i18n?.resolvedLanguage ||
        i18n?.language ||
        getQueryVariable('language') ||
        window.lang ||
        'zh';
    const captchaLanguage = useMemo(
        () => getCaptchaLanguage(currentLanguage),
        [currentLanguage]
    );
    const acceptLanguage = useMemo(
        () => captchaLanguage === 'tw' ? 'zh-TW,zh' : codeToLocale(captchaLanguage),
        [captchaLanguage]
    );

    const getInstance = (instance) => {
        captcha = instance;
    };

    const captchaVerifyCallback = async (captchaVerifyParam) => {
        // 1.向后端发起业务请求，获取验证码验证结果和业务结果
        // const result = await xxxx('http://您的业务请求地址', {
        //     captchaVerifyParam: captchaVerifyParam, // 验证码参数
        //     yourBizParam... // 业务参数
        // });
        // return {
        //  captchaResult: true, // 验证码验证是否通过，boolean类型，必选
        //  bizResult: true, // 业务验证是否通过，boolean类型，可选；若为无业务验证结果的场景，bizResult可以为空
        // }
        const res = await verifyIntelligentCaptcha(
            { captchaVerifyParam, phone: isUsedForApp ? phoneNemberFromApp : phoneRef.current },
            acceptLanguage
        )
        let result = res.result;
        if (!res.success) {
            res.errorInfo && message.error({
                content: res.errorInfo,
                className: mode === 'embed'?'capMessage':'',
            })
            return {
                captchaResult: false,
                bizResult: false,
            }
        }
        if (!result.verifyResult) {
            result.VerifyError && message.error({
                content: result.VerifyError,
                className: mode === 'embed'?'capMessage':'',
            })
            return {
                captchaResult: false,
                bizResult: false,
            }
        } else {
            if (isUsedForApp) {
                window.flutter_inappwebview?.callHandler('appGetAliyunCaptchaResult', {
                    result
                })
            } else {
                callBack?.(result)
            }
            return {
                captchaResult: true,
                bizResult: true,
            }
        }

    }
    // 验证通过后调用
    const onBizResultCallback = () => {
        console.log('onBizResultCallback');
        if (!isUsedForApp) {
            callBack?.()
        }
    }

    useEffect(() => {
        window.initAliyunCaptcha({
            SceneId: '14jp0f95', // 场景ID。根据步骤二新建验证场景后，您可以在验证码场景列表，获取该场景的场景ID
            prefix: '1cbfq0', // 身份标。开通阿里云验证码2.0后，您可以在控制台概览页面的实例基本信息卡片区域，获取身份标
            mode, // 验证码模式。popup表示要集成的验证码模式为弹出式。无需修改
            element: `#${element}` , // 页面上预留的渲染验证码的元素，与原代码中预留的页面元素保持一致。 // '#captcha-element'
            button: button, // 触发验证码弹窗的元素。button表示单击登录按钮后，触发captchaVerifyCallback函数。您可以根据实际使用的元素修改element的值
            captchaVerifyCallback: captchaVerifyCallback, // 业务请求(带验证码校验)回调函数，无需修改
            onBizResultCallback: onBizResultCallback, // 业务请求结果回调函数，无需修改
            getInstance: getInstance, // 绑定验证码实例函数，无需修改
            slideStyle: {
                width: 360,
                height: 40,
            }, // 滑块验证码样式，支持自定义宽度和高度，单位为px。其中，width最小值为320 px
            immediate, // 完成验证后，是否立即发送验证请求（调用captchaVerifyCallback函数）
            language: captchaLanguage, // 验证码语言类型，支持简体中文（cn）、繁体中文（tw）、英文（en）、日文（ja）
            upLang: ALIYUN_CAPTCHA_COPY,
        });

        return () => {
            // 必须删除相关元素，否则再次mount多次调用 initAliyunCaptcha 会导致多次回调 captchaVerifyCallback
            document.getElementById('aliyunCaptcha-mask')?.remove();
            document.getElementById('aliyunCaptcha-window-popup')?.remove();
        }

    }, [button, captchaLanguage, mode, immediate]);

    return (
        <div className='aliyun-captcha-container'>
            <div id={element}></div>
        </div>
    )
}

export default App;
